'use client';
/*
 * Documentation:
 * Text Area — https://app.subframe.com/6fc7f5fbb098/library?component=Text+Area_4ec05ee8-8f1c-46b2-b863-5419aa7f5cce
 */

import React from 'react';
import * as SubframeUtils from '../utils';

interface InputProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'placeholder'
> {
  placeholder?: React.ReactNode;
  value?: string;
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  autoResize?: boolean;
  className?: string;
}

const Input = React.forwardRef<HTMLTextAreaElement, InputProps>(function Input(
  { placeholder, className, autoResize = false, value, ...otherProps }: InputProps,
  ref,
) {
  const innerRef = React.useRef<HTMLTextAreaElement | null>(null);

  const setRefs = (node: HTMLTextAreaElement | null) => {
    innerRef.current = node;

    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
    }
  };

  React.useEffect(() => {
    if (!autoResize) {
      return;
    }

    const element = innerRef.current;

    if (!element) {
      return;
    }

    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  }, [autoResize, value]);

  return (
    <textarea
      className={SubframeUtils.twClassNames(
        'text-body font-body text-default-font min-h-[120px] w-full border-none bg-transparent px-3 py-2.5 outline-none placeholder:text-neutral-400',
        { 'resize-none overflow-hidden': autoResize },
        className,
      )}
      placeholder={placeholder as string}
      ref={setRefs}
      value={value}
      {...otherProps}
    />
  );
});

interface TextAreaRootProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  disabled?: boolean;
  error?: boolean;
  variant?: 'outline' | 'filled';
  label?: React.ReactNode;
  helpText?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}

const TextAreaRoot = React.forwardRef<HTMLLabelElement, TextAreaRootProps>(function TextAreaRoot(
  {
    disabled = false,
    error = false,
    variant = 'outline',
    label,
    helpText,
    children,
    className,
    ...otherProps
  }: TextAreaRootProps,
  ref,
) {
  return (
    <label
      className={SubframeUtils.twClassNames(
        'group/4ec05ee8 flex flex-col items-start gap-1',
        className,
      )}
      ref={ref}
      {...otherProps}
    >
      {label ? (
        <span className="text-caption-bold font-caption-bold text-default-font">{label}</span>
      ) : null}
      {children ? (
        <div
          className={SubframeUtils.twClassNames(
            'bg-default-background flex w-full shrink-0 grow basis-0 flex-col items-start rounded-[8px] border border-solid border-neutral-300 transition-all duration-200',
            {
              'group-hover/4ec05ee8:border-neutral-border group-focus-within/4ec05ee8:bg-default-background border border-solid border-neutral-200 bg-neutral-100 group-hover/4ec05ee8:border group-hover/4ec05ee8:border-solid':
                variant === 'filled',
              'border-error-600': error,
              'cursor-not-allowed border-neutral-200 bg-neutral-50': disabled,
              'group-focus-within/4ec05ee8:border-brand-600 group-focus-within/4ec05ee8:ring-brand-600/10 group-focus-within/4ec05ee8:ring-4':
                !error && !disabled,
              'group-focus-within/4ec05ee8:border-error-600 group-focus-within/4ec05ee8:ring-error-600/10 group-focus-within/4ec05ee8:ring-4':
                error && !disabled,
            },
          )}
        >
          {children}
        </div>
      ) : null}
      {helpText ? (
        <span
          className={SubframeUtils.twClassNames('text-caption font-caption text-subtext-color', {
            'text-error-700': error,
          })}
        >
          {helpText}
        </span>
      ) : null}
    </label>
  );
});

export const TextArea = Object.assign(TextAreaRoot, {
  Input,
});
